import React, { useEffect } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { blogArticles } from '../lib/content';

const pageText = {
  de: {
    backToBlog: 'Zurück zum Blog',
    ctaTitle: 'Interesse an unseren Dienstleistungen?',
    ctaText: 'Kontaktieren Sie uns für eine unverbindliche Beratung zu Ihrem Web-Projekt.',
    ctaButton: 'Jetzt Kontakt aufnehmen',
    shareText: 'Teilen Sie diesen Artikel',
    share: 'Teilen'
  },
  it: {
    backToBlog: 'Torna al Blog',
    ctaTitle: 'Interessati ai nostri servizi?',
    ctaText: 'Contattateci per una consulenza senza impegno sul vostro progetto web.',
    ctaButton: 'Contattaci ora',
    shareText: 'Condividi questo articolo',
    share: 'Condividi'
  },
  en: {
    backToBlog: 'Back to Blog',
    ctaTitle: 'Interested in our services?',
    ctaText: 'Contact us for a non-binding consultation about your web project.',
    ctaButton: 'Contact us now',
    shareText: 'Share this article',
    share: 'Share'
  },
  es: {
    backToBlog: 'Volver al Blog',
    ctaTitle: '¿Interesado en nuestros servicios?',
    ctaText: 'Contáctenos para una consulta sin compromiso sobre su proyecto web.',
    ctaButton: 'Contáctanos ahora',
    shareText: 'Comparte este artículo',
    share: 'Compartir'
  }
};

export default function BlogArticlePage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const location = useLocation();
  const text = pageText[language] || pageText.de;

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const article = blogArticles.find(a => a.slug === slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const content = article.content[language] || article.content.de;

  // Get locale for date formatting
  const getLocale = () => {
    switch(language) {
      case 'it': return 'it-CH';
      case 'en': return 'en-GB';
      case 'es': return 'es-ES';
      default: return 'de-CH';
    }
  };

  // Simple markdown-like rendering for the content
  const renderContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
      // H2
      if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl md:text-3xl font-bold text-white font-['Manrope'] mt-12 mb-6">
            {line.replace('## ', '')}
          </h2>
        );
      }
      // H3
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-semibold text-white font-['Manrope'] mt-8 mb-4">
            {line.replace('### ', '')}
          </h3>
        );
      }
      // List item
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="text-zinc-400 ml-4 mb-2">
            {line.replace('- ', '')}
          </li>
        );
      }
      // Bold text (simple)
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={index} className="text-zinc-400 leading-relaxed mb-4">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
            )}
          </p>
        );
      }
      // Empty line
      if (line.trim() === '') {
        return <br key={index} />;
      }
      // Regular paragraph
      return (
        <p key={index} className="text-zinc-400 leading-relaxed mb-4">
          {line}
        </p>
      );
    });
  };

  return (
    <main className="min-h-screen bg-[#050505] pt-24" data-testid="blog-article-page">
      <article className="max-w-[800px] mx-auto px-4 md:px-8 py-16 md:py-24">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-12"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            {text.backToBlog}
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-zinc-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(article.publishedAt).toLocaleDateString(getLocale(), {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {article.readTime}
            </span>
            <span className="px-3 py-1 bg-zinc-800 rounded text-zinc-400">
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#8B5CF6] font-['Manrope'] leading-tight mb-6">
            {article.title[language] || article.title.de}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-zinc-400 leading-relaxed">
            {article.excerpt[language] || article.excerpt.de}
          </p>
        </motion.header>

        {/* Featured Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="aspect-[16/9] overflow-hidden bg-zinc-900 mb-12"
        >
          <img
            src={article.image}
            alt={article.title[language] || article.title.de}
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-invert max-w-none"
          data-testid="article-content"
        >
          {renderContent(content)}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 p-8 bg-zinc-900/50 border border-zinc-800"
        >
          <h3 className="text-xl font-semibold text-white font-['Manrope'] mb-3">
            {text.ctaTitle}
          </h3>
          <p className="text-zinc-400 mb-6">
            {text.ctaText}
          </p>
          <Link
            to="/#kontakt"
            className="inline-flex items-center gap-2 bg-white text-black rounded-full px-8 py-4 font-medium hover:bg-zinc-200 transition-colors"
            data-testid="article-cta"
          >
            {text.ctaButton}
          </Link>
        </motion.div>

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-zinc-800 flex items-center justify-between">
          <span className="text-zinc-500 text-sm">{text.shareText}</span>
          <button
            onClick={() => navigator.share?.({ 
              title: article.title[language] || article.title.de,
              url: window.location.href 
            })}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="share-button"
          >
            <Share2 className="w-4 h-4" />
            {text.share}
          </button>
        </div>
      </article>
    </main>
  );
}
